import { NativeModules, NativeEventEmitter } from 'react-native';
import { WINDOWS_VOICE_HTML } from './windowsVoicePage';

let activePeer = null, nextPeer = 0;
class WindowsVoicePeerConnection {
  constructor() {
    if (activePeer) throw new Error('A voice connection is already active. End it before starting another.');
    this.native = NativeModules.WindowsVoice;
    if (!this.native) throw new Error('Windows voice support is unavailable. Rebuild the app.');
    activePeer = this;
    this.id = `voice-${Date.now()}-${++nextPeer}`;
    this.connectionState = 'new'; this.closed = false; this.level = 0;
    this.subscription = new NativeEventEmitter(this.native).addListener('onWindowsVoiceEvent', message => {
      if (message.id !== this.id || this.closed) return;
      let event; try { event = JSON.parse(message.json); } catch { return; }
      if (event.type === 'connection') { this.connectionState = event.state; this.onconnectionstatechange?.(); }
      else if (event.type === 'level') this.level = Number.isFinite(event.level) ? Math.max(0, Math.min(1, event.level)) : 0;
      else if (event.type === 'error') {
        this.channel?.onerror?.(new Error(event.message)); this.connectionState = 'failed'; this.onconnectionstatechange?.();
      } else if (this.channel && ['open', 'close', 'message'].includes(event.type)) {
        if (event.type === 'open') this.channel.readyState = 'open';
        if (event.type === 'close') this.channel.readyState = 'closed';
        this.channel[`on${event.type}`]?.({ data: event.data });
      }
    });
    this.ready = this.native.prepareVoice(this.id, WINDOWS_VOICE_HTML).then(() => {
      if (this.closed) throw new Error('Voice connection closed');
    });
    this.ready.catch(() => {});
  }
  async command(command, payload = {}) {
    await this.ready;
    if (this.closed) throw new Error('Voice connection closed');
    return JSON.parse(await this.native.voiceCommand(this.id, command, JSON.stringify(payload)));
  }
  sendCommand(command, payload) { this.command(command, payload).catch(error => { if (!this.closed) this.channel?.onerror?.(error); }); }
  microphone() {
    if (this.microphonePending) return this.microphonePending;
    const pending = this.acquireMicrophone(); this.microphonePending = pending;
    pending.finally(() => { if (this.microphonePending === pending) this.microphonePending = null; }).catch(() => {});
    return pending;
  }
  async acquireMicrophone() {
    if (this.stream?.active) return this.stream;
    await this.command('microphone');
    if (this.closed) throw new Error('Voice connection closed');
    let enabled = true; const peer = this;
    const track = { readyState: 'live', get enabled() { return enabled; }, set enabled(value) { enabled = !!value; peer.sendCommand('mute', { muted: !enabled }); },
      stop() { if (track.readyState === 'ended') return; track.readyState = 'ended'; if (!peer.closed) peer.sendCommand('stopInput'); } };
    this.stream = { get active() { return track.readyState === 'live'; }, getTracks: () => [track], getAudioTracks: () => [track] };
    return this.stream;
  }
  addTrack() {} // The app-owned browser attaches its acquired microphone track.
  createDataChannel() {
    this.channel = { readyState: 'connecting', send: data => this.sendCommand('send', { data }), close: () => { this.channel.readyState = 'closed'; } };
    return this.channel;
  }
  createOffer() { return this.command('offer'); }
  setLocalDescription(offer) { return this.command('local', offer); }
  setRemoteDescription(answer) { return this.command('answer', answer); }
  getSenders() { return this.stream?.getTracks().map(track => ({ track })) || []; }
  close() {
    if (this.closed) return;
    this.closed = true; this.stream?.getTracks().forEach(track => track.stop()); this.subscription.remove();
    this.native.closeVoice(this.id); this.connectionState = 'closed';
    if (activePeer === this) activePeer = null;
  }
}
export function getRealtimeWebRTCAdapter() {
  return { RTCPeerConnection: WindowsVoicePeerConnection, RTCSessionDescription: null, isNative: true,
    mediaDevices: { getUserMedia: () => activePeer ? activePeer.microphone() : Promise.reject(new Error('Start a voice connection before acquiring the microphone.')) } };
}
export const getWindowsVoiceInputVolume = () => activePeer?.level || 0;
export async function configureNativeRealtimeAudio() {}
export async function restoreNativeRealtimeAudio() {}
export function subscribeNativeRealtimeAudioFocus() { return null; }
